import styled from "@emotion/styled";
import type { ReactNode } from "react";

import { Button } from "../../buttons";
import { LoadingIndicator } from "../../common";
import { Icon } from "../../icons";
import { SidebarSectionContentDescription, SidebarSectionHeading } from "../sections";
import { MainPanelItem } from "./MainPanelItem";

const Body = styled.div`
  display: flex;
  flex-direction: column;

  background-color: ${({ theme }) => theme.surface};

  padding: 16px;
`;

const Label = styled.div`
  display: flex;
  justify-content: center;
`;

const AddContainer = styled.div`
  display: grid;
  border-bottom: solid 1px ${({ theme }) => theme.outlineSoft};
  width: 100%;

  & button {
    padding: 0;
  }

  ${MainPanelItem} {
    font-size: ${({ theme }) => theme.fontSize};
  }

  ${MainPanelItem} div:nth-of-type(1) {
    justify-self: center;
  }
`;

type AddButton = {
  label?: ReactNode;
  hidden?: boolean;
  disabled?: boolean;
  position?: "before" | "after";
  onAdd?: () => void;
};

type Props = {
  title?: ReactNode;
  description?: ReactNode;
  addButton?: AddButton;
  children: ReactNode;
  isFetching?: boolean;
};

export const MainPanel = ({ addButton, children, title, description, isFetching = false }: Props) => {
  const { position: addButtonPosition, hidden, ...addButtonProps } = addButton || {};

  return (
    <div data-test="content-container">
      {(title || description) && (
        <Body data-test="content-header">
          <SidebarSectionHeading>
            <span>{title}</span>
            {!!isFetching && <LoadingIndicator />}
          </SidebarSectionHeading>
          <SidebarSectionContentDescription data-test="content-description">
            {description}
          </SidebarSectionContentDescription>
        </Body>
      )}

      {!!addButton && !hidden && addButtonPosition === "before" && <MainPanelAddItemButton {...addButtonProps} />}

      <div data-test="list">{children}</div>

      {!!addButton && !hidden && addButtonPosition !== "before" && <MainPanelAddItemButton {...addButtonProps} />}
    </div>
  );
};

export const MainPanelAddItemButton = ({ onAdd, disabled, label }: Pick<AddButton, "disabled" | "onAdd" | "label">) => (
  <AddContainer data-test="add-list-item-container">
    <Button type="button" onClick={() => onAdd?.()} disabled={disabled} data-test="add-list-item-button">
      <MainPanelItem>
        <Label>{label}</Label>
        <Icon icon="angle-right" size={14} />
      </MainPanelItem>
    </Button>
  </AddContainer>
);
